import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '@/game/config';

/**
 * "Deep Field" — the space backdrop. A galactic band runs lower-left to upper-right with
 * violet-blue arms, a pale violet core and a dark dust lane cutting through it; star density
 * clusters on the band and thins away from it, which is what sells the depth.
 *
 * The band is expensive to draw (hundreds of soft radial blobs), so it is rendered ONCE into a
 * cached canvas texture and shown as a single image — cheaper at runtime than the old version's
 * ~170 Graphics operations, and it survives scene restarts for free.
 *
 * On top of that still image sit the moving parts: a seamless star layer drifting very slowly,
 * a handful of twinkling stars, and — where the caller asks for them — occasional shooting stars.
 *
 * The centre of the frame is deliberately the quietest part: the menu's title, Play button and
 * pills all live there, and nothing they sit on may become the brightest thing on screen.
 *
 * Sits behind everything (depth -10). Used for the "space" background and the menu.
 */

const BASE_KEY = 'deepfield-base';
const DRIFT_KEY = 'deepfield-drift';
const STREAK_KEY = 'deepfield-streak';
const SPIKE_KEY = 'deepfield-spike';

/** Edge of the bright-star sprite. Its halo and spikes are drawn to fit inside this box. */
const SPIKE_TILE = 64;

/**
 * The menu's centre column, kept free of bright stars. Spans the title down to the progress bar
 * and is a little wider than the widest settings pill (MenuScene puts that at roughly x 440–840).
 */
const UI_COLUMN_LEFT = 380;
const UI_COLUMN_RIGHT = GAME_WIDTH - 380;
const UI_BAND_TOP = 90;
const UI_BAND_BOTTOM = 660;

/** Tile edge for the drifting star layer. Powers of two wrap without a seam. */
const DRIFT_TILE = 512;

/** Star colours: mostly white, a few blue-white and warm ones, like a real field. */
const STAR_TINTS = ['#ffffff', '#ffffff', '#ffffff', '#dce8ff', '#bcd2ff', '#ffe6c4', '#ffcfa2'];

export interface StarfieldOptions {
  /**
   * Let the occasional meteor cross the top of the frame. Off by default: during a round a
   * moving streak reads as a game object, and a child mid-word does not need the distraction.
   */
  shootingStars?: boolean;
}

/** Deterministic RNG (mulberry32) — the sky must be identical on every construction, otherwise
 *  a scene.restart (e.g. after a language switch) reshuffles it and reads as a flicker. */
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Roughly normal, mean 0, useful range about ±2 — for scattering around the band's centreline. */
function gauss(r: () => number): number {
  return (r() + r() + r() + r() - 2) * 0.9;
}

/** A soft elliptical cloud. `color` carries the literal ALPHA placeholder so one string can
 *  serve all three gradient stops. */
function blob(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, rx: number, ry: number, rot: number,
  color: string, alpha: number,
): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rot);
  ctx.scale(1, ry / rx);
  const g = ctx.createRadialGradient(0, 0, 0, 0, 0, rx);
  g.addColorStop(0, color.replace('ALPHA', String(alpha)));
  g.addColorStop(0.55, color.replace('ALPHA', String(alpha * 0.45)));
  g.addColorStop(1, color.replace('ALPHA', '0'));
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(0, 0, rx, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function dot(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, r: number, color: string, alpha: number,
): void {
  ctx.globalAlpha = alpha;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = 1;
}

/**
 * A bright star: a soft halo with crossed diffraction spikes, the pair set on the diagonal.
 * The spikes are strokes of even thickness — plain, and that is the point; the tapered
 * needle-ray version read as fussy next to the rest of the field.
 */
function spikeStar(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  const g = ctx.createRadialGradient(x, y, 0, x, y, r * 7);
  g.addColorStop(0, 'rgba(255,255,255,.95)');
  g.addColorStop(0.18, 'rgba(255,255,255,.28)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(x, y, r * 7, 0, Math.PI * 2);
  ctx.fill();

  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(Math.PI / 4);
  ctx.strokeStyle = 'rgba(255,255,255,.45)';
  ctx.lineWidth = Math.max(0.6, r * 0.34);
  ctx.beginPath();
  ctx.moveTo(-r * 6.5, 0); ctx.lineTo(r * 6.5, 0);
  ctx.moveTo(0, -r * 6.5); ctx.lineTo(0, r * 6.5);
  ctx.stroke();
  ctx.restore();

  dot(ctx, x, y, r * 1.1, '#fff', 1);
}

/** The still layer: ground, galactic band, dust lane, field stars. */
function drawDeepField(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  const r = rng(2027);

  const ground = ctx.createLinearGradient(0, 0, 0, h);
  ground.addColorStop(0, '#030409');
  ground.addColorStop(0.38, '#060616');
  ground.addColorStop(0.72, '#0a071c');
  ground.addColorStop(1, '#120920');
  ctx.fillStyle = ground;
  ctx.fillRect(0, 0, w, h);

  // Band centreline, lower-left to upper-right. `env` is the width/brightness envelope: fat and
  // bright in the middle, tapering to nothing at both ends so it never just stops at an edge.
  const P = (t: number) => ({ x: w * (-0.04 + t * 1.08), y: h * (0.92 - t * 0.8) });
  const env = (t: number) => Math.sin(Math.PI * Math.min(1, Math.max(0, t))) ** 0.7;
  const NX = Math.cos(0.94);   // unit normal to the band, for scattering across it
  const NY = Math.sin(0.94);

  ctx.globalCompositeOperation = 'lighter';
  const cool = ['rgba(78,70,195,ALPHA)', 'rgba(48,96,180,ALPHA)'];
  const warm = ['rgba(158,92,196,ALPHA)', 'rgba(128,88,214,ALPHA)', 'rgba(186,138,150,ALPHA)'];
  for (let i = 0; i < 190; i++) {
    const t = r();
    const c = P(t);
    const e = env(t);
    const off = gauss(r) * (34 + 150 * e);
    const near = 1 - Math.min(1, Math.abs(off) / (180 * e + 40));
    const pal = t > 0.34 && t < 0.72 ? warm : cool;
    blob(
      ctx, c.x + NX * off, c.y + NY * off,
      (45 + 195 * e) * (0.5 + r()), (26 + 92 * e) * (0.5 + r()), -0.63,
      pal[(r() * pal.length) | 0], 0.010 + 0.026 * near * e,
    );
  }
  // Galactic core, plus one cool and one warm knot further along the arms.
  blob(ctx, w * 0.55, h * 0.44, w * 0.28, h * 0.115, -0.63, 'rgba(214,210,255,ALPHA)', 0.058);
  blob(ctx, w * 0.55, h * 0.44, w * 0.16, h * 0.065, -0.63, 'rgba(255,226,238,ALPHA)', 0.062);
  blob(ctx, w * 0.2, h * 0.76, w * 0.14, h * 0.065, -0.63, 'rgba(130,148,240,ALPHA)', 0.052);
  blob(ctx, w * 0.86, h * 0.2, w * 0.13, h * 0.058, -0.63, 'rgba(190,120,225,ALPHA)', 0.05);

  // Dark dust lane through the band — without it the band is a smear rather than a galaxy.
  ctx.globalCompositeOperation = 'source-over';
  for (let i = 0; i < 70; i++) {
    const t = r();
    const c = P(t);
    const e = env(t);
    const off = gauss(r) * 22 + 18 * e;
    blob(
      ctx, c.x + NX * off, c.y + NY * off,
      (70 + 120 * e) * (0.6 + r()), (12 + 26 * e) * (0.5 + r()), -0.63,
      'rgba(4,5,13,ALPHA)', 0.22 * e + 0.06,
    );
  }
  // Keep the centre column — title, Play button, pills — the quietest part of the frame.
  blob(ctx, w * 0.5, h * 0.46, w * 0.52, h * 0.56, 0, 'rgba(3,5,12,ALPHA)', 0.2);

  // Field stars: a bit over half of them cluster on the band, the rest are scattered.
  for (let i = 0; i < 620; i++) {
    let x: number;
    let y: number;
    let bright: number;
    if (r() < 0.55) {
      const t = r();
      const c = P(t);
      const off = gauss(r) * (50 + 180 * env(t));
      x = c.x + NX * off;
      y = c.y + NY * off;
      bright = 0.3 + r() * 0.55;
    } else {
      x = r() * w;
      y = r() * h;
      bright = 0.16 + r() * 0.45;
    }
    dot(ctx, x, y, 0.35 + r() * 1.25, STAR_TINTS[(r() * STAR_TINTS.length) | 0], bright);
  }
  // The bright spiked stars are NOT baked in here — they are sprites on top so they can pulse.
}

/**
 * The drifting layer: a small tile of sparse stars. Every star near an edge is drawn again on
 * the opposite side, so the tile repeats without a visible seam when it scrolls.
 */
function drawDriftTile(ctx: CanvasRenderingContext2D, size: number): void {
  const r = rng(90210);
  ctx.clearRect(0, 0, size, size);
  for (let i = 0; i < 46; i++) {
    const x = r() * size;
    const y = r() * size;
    const rad = 0.4 + r() * 1.0;
    const tint = STAR_TINTS[(r() * STAR_TINTS.length) | 0];
    const alpha = 0.18 + r() * 0.4;
    for (const dx of [-size, 0, size]) {
      for (const dy of [-size, 0, size]) {
        dot(ctx, x + dx, y + dy, rad, tint, alpha);
      }
    }
  }
}

/** The bright-star sprite: halo plus the four-point diffraction spikes, centred in its tile. */
function drawSpikeTile(ctx: CanvasRenderingContext2D, size: number): void {
  ctx.clearRect(0, 0, size, size);
  spikeStar(ctx, size / 2, size / 2, size / 16);
}

/** A short white streak, fading to nothing at the tail. Rotated to the meteor's heading. */
function drawStreak(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  const g = ctx.createLinearGradient(0, 0, w, 0);
  g.addColorStop(0, 'rgba(255,255,255,0)');
  g.addColorStop(0.75, 'rgba(255,255,255,.85)');
  g.addColorStop(1, 'rgba(255,255,255,1)');
  ctx.fillStyle = g;
  ctx.fillRect(0, h / 2 - 0.75, w, 1.5);
}

/** Draw into a canvas texture once and keep it — repeat constructions reuse it. */
function ensureTexture(
  scene: Phaser.Scene, key: string, w: number, h: number,
  paint: (ctx: CanvasRenderingContext2D) => void,
): void {
  if (scene.textures.exists(key)) return;
  const tex = scene.textures.createCanvas(key, w, h);
  if (!tex) return;
  const ctx = tex.getContext();
  if (ctx) paint(ctx);
  tex.refresh();
}

export class Starfield extends Phaser.GameObjects.Container {
  constructor(scene: Phaser.Scene, opts: StarfieldOptions = {}) {
    super(scene, 0, 0);
    this.setDepth(-10);

    ensureTexture(scene, BASE_KEY, GAME_WIDTH, GAME_HEIGHT, (ctx) => drawDeepField(ctx, GAME_WIDTH, GAME_HEIGHT));
    ensureTexture(scene, DRIFT_KEY, DRIFT_TILE, DRIFT_TILE, (ctx) => drawDriftTile(ctx, DRIFT_TILE));
    ensureTexture(scene, STREAK_KEY, 120, 4, (ctx) => drawStreak(ctx, 120, 4));
    ensureTexture(scene, SPIKE_KEY, SPIKE_TILE, SPIKE_TILE, (ctx) => drawSpikeTile(ctx, SPIKE_TILE));

    const rnd = rng(4711);

    // Still layer.
    this.add(scene.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, BASE_KEY));

    // Drift layer: two tweens at very different speeds give a slow diagonal crawl. The tile is
    // square and the tween spans exactly one tile, so the wrap is invisible.
    const drift = scene.add.tileSprite(
      GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, DRIFT_KEY,
    ).setAlpha(0.85);
    this.add(drift);
    scene.tweens.add({
      targets: drift, tilePositionX: DRIFT_TILE, duration: 300000, repeat: -1, ease: 'Linear',
    });
    scene.tweens.add({
      targets: drift, tilePositionY: -DRIFT_TILE, duration: 900000, repeat: -1, ease: 'Linear',
    });

    // The big spiked stars, breathing. Much slower than the small twinkles below and never fully
    // out, so the effect reads as depth rather than as something blinking for attention.
    //
    // Two placement rules. They keep out of the centre column entirely — that is where the menu
    // stacks its title, Play button and pills, and a pulsing star behind a button competes with
    // it. And they hold a minimum separation from each other: left to pure chance they clump into
    // a constellation, which reads as a mistake rather than as a sky.
    const placed: { x: number; y: number }[] = [];
    for (let i = 0; i < 11; i++) {
      let x = 0;
      let y = 0;
      for (let attempt = 0; attempt < 24; attempt++) {
        y = 40 + rnd() * (GAME_HEIGHT - 80);
        if (y > UI_BAND_TOP && y < UI_BAND_BOTTOM) {
          // Beside the menu column: draw from the left margin or the right one.
          const left = rnd() < 0.5;
          x = left
            ? 40 + rnd() * (UI_COLUMN_LEFT - 40)
            : UI_COLUMN_RIGHT + rnd() * (GAME_WIDTH - 40 - UI_COLUMN_RIGHT);
        } else {
          x = 40 + rnd() * (GAME_WIDTH - 80);
        }
        if (placed.every((p) => Phaser.Math.Distance.Between(p.x, p.y, x, y) > 150)) break;
      }
      placed.push({ x, y });
      const spike = scene.add.image(x, y, SPIKE_KEY).setScale(0.30 + rnd() * 0.30);
      this.add(spike);
      const rest = 0.5 + rnd() * 0.2;
      spike.setAlpha(rest);
      scene.tweens.add({
        targets: spike,
        alpha: 1,
        scale: spike.scale * 1.18,
        duration: 2800 + rnd() * 2600,
        delay: rnd() * 3000,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    // A few smaller stars that twinkle quicker.
    for (let i = 0; i < 16; i++) {
      const star = scene.add.circle(
        24 + rnd() * (GAME_WIDTH - 48),
        24 + rnd() * (GAME_HEIGHT - 48),
        1.3 + rnd() * 1.3,
        0xffffff,
        0.6 + rnd() * 0.35,
      );
      this.add(star);
      scene.tweens.add({
        targets: star,
        alpha: 0.2,
        duration: 900 + rnd() * 900,
        delay: rnd() * 1400,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    if (opts.shootingStars) this.scheduleShootingStar(scene, rnd);

    scene.add.existing(this);
  }

  /**
   * One meteor at a time, across the upper third, then a long wait before the next. Deliberately
   * rare: the point is that a child who keeps looking is occasionally rewarded, not that the
   * screen is busy.
   */
  private scheduleShootingStar(scene: Phaser.Scene, rnd: () => number): void {
    const next = (): void => {
      scene.time.delayedCall(6000 + rnd() * 9000, () => {
        // The scene may have been shut down while the timer was pending.
        if (!this.scene || !this.active) return;

        const fromLeft = rnd() < 0.5;
        const y = GAME_HEIGHT * (0.06 + rnd() * 0.30);
        const travel = GAME_WIDTH * (0.32 + rnd() * 0.22);
        const dip = GAME_HEIGHT * (0.06 + rnd() * 0.08);
        const x = fromLeft ? -80 + rnd() * GAME_WIDTH * 0.35
          : GAME_WIDTH + 80 - rnd() * GAME_WIDTH * 0.35;
        const dx = fromLeft ? travel : -travel;

        const streak = scene.add.image(x, y, STREAK_KEY)
          .setOrigin(1, 0.5)
          .setAlpha(0)
          .setScale(0.9 + rnd() * 0.7);
        streak.setRotation(Math.atan2(dip, dx));
        if (!fromLeft) streak.setFlipX(true);
        this.add(streak);

        scene.tweens.add({
          targets: streak,
          x: x + dx,
          y: y + dip,
          duration: 620 + rnd() * 260,
          ease: 'Sine.easeIn',
          alpha: { from: 0, to: 0.9, duration: 160, yoyo: true, hold: 180 },
          onComplete: () => streak.destroy(),
        });

        next();
      });
    };
    next();
  }
}
